import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  getProjectHandlers,
  getProjectUrl,
} from '../../store/utils/api'

import {
  BookingState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  IData_Booking_Settings,
  IRecord_Booking_Settings,
  IQuery_Booking_List,
  IElasticSearch_Source_Booking,
  IRecord_Booking_Core_Editable,
  IRecord_Booking_Core_Viewable,
  IData_Calendar_Admin_Day,
  IData_Calendar_Public_Day,
  IData_Booking_Calendar_Export_Links,
  IData_Booking_Reporting,
  ICreateBookingCheckEmailResponse,
} from '@kaidam/fc-shared/src/types/schema/booking'

import {
  IRecord_CRMTag,
} from '@kaidam/fc-shared/src/types/schema/crm'

import {
  IData_Paginated_Query_Wrapper,
  IData_Paginated_Results_Wrapper,
} from '@kaidam/fc-shared/src/types/pagination'

import {
  IElasticSearchResult,
} from '@kaidam/fc-shared/src/types/elasticsearch'

import {
  PayloadSearch,
} from '../../types/booking'

import {
  getPaginationQueryParams,
} from '@kaidam/fc-shared/src/utils/pagination'

import {
  stripObjectValues,
} from '@kaidam/fc-shared/src/utils/object'

import {
  IQuery_Org_Scope,
} from '@kaidam/fc-shared/src/types/utils'

import {
  NetworkThunk,
} from '../utils/network'

import {
  PAGINATION_RESULTS_DEFAULT,
  ROLE_SCOPE_DESCENDENTS,
  BOOKING_DEFAULT_CALENDAR_DAYS,
  BOOKING_DEFAULT_CALENDAR_LENGTH,
  BOOKING_SETTINGS_MODE_HISTORIC,
} from '@kaidam/fc-shared/src/constants'

import authSelectors from '../selectors/auth'
import projectSelectors from '../selectors/project'

const prefix = 'booking'

/*

  initial state

*/
const initialState: BookingState = {
  settings: [],
  historicSettings: [],
  bookingData: [],
  bookingPagination: PAGINATION_RESULTS_DEFAULT,
  reportingData: [],
  searchResults: [],
  adminCalendar: [],
  publicCalendar: [],
  calendarExportLinks: undefined,
  tags: [],
}

/*

  reducers

*/
const setSettings: ReducerHandler<BookingState, IRecord_Booking_Settings[]> = (state, action) => {
  state.settings = action.payload
}

const setHistoricSettings: ReducerHandler<BookingState, IRecord_Booking_Settings[]> = (state, action) => {
  state.historicSettings = action.payload
}

const setBookingData: ReducerHandler<BookingState, IData_Paginated_Results_Wrapper<IRecord_Booking_Core_Viewable>> = (state, action) => {
  const {
    data,
    pagination,
  } = action.payload
  state.bookingData = data
  state.bookingPagination = pagination
}

const setReportingData: ReducerHandler<BookingState, IData_Booking_Reporting[]> = (state, action) => {
  state.reportingData = action.payload
}

const setSearchResults: ReducerHandler<BookingState, IElasticSearchResult<IElasticSearch_Source_Booking>[]> = (state, action) => {
  state.searchResults = action.payload
}

const setAdminCalendar: ReducerHandler<BookingState, IData_Calendar_Admin_Day[]> = (state, action) => {
  state.adminCalendar = action.payload
}

const setPublicCalendar: ReducerHandler<BookingState, IData_Calendar_Public_Day[]> = (state, action) => {
  state.publicCalendar = action.payload
}

const setCalendarExportLinks: ReducerHandler<BookingState, IData_Booking_Calendar_Export_Links> = (state, action) => {
  state.calendarExportLinks = action.payload
}

const setTags: ReducerHandler<BookingState, IRecord_CRMTag[]> = (state, action) => {
  state.tags = action.payload
}

/*

  thunks

*/


/*

  org

*/

// load just the org level settings for the whole project
const loadAllOrgSettings: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_Booking_Settings[]>(`/booking/settings/project`)
  dispatch(actions.setSettings(data))
}

// load only settings for a single org - including user settings
const loadSingleOrgSettings: ThunkHandler<{
  org: string,
}, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_Booking_Settings[]>(`/booking/settings/org/${query.org}`)
  dispatch(actions.setSettings(data))
}

const loadSingleOrgSettingsHistoric: ThunkHandler<{
  org: string,
}, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_Booking_Settings[]>(`/booking/settings/org/${query.org}`, {
    params: {
      mode: BOOKING_SETTINGS_MODE_HISTORIC,
    }
  })
  dispatch(actions.setHistoricSettings(data))
}

// this assumes that we have already loaded in the orgs settings
// so have the existing value in memory so we can merge the new value
const updateSettings: ThunkHandler<{
  org: string,
  user: string,
  data: Partial<IData_Booking_Settings>,
}, boolean> = ({
  org,
  user,
  data,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put(`/booking/settings/${org}/${user}`, data)
  return true
}

// this assumes that we have already loaded in the orgs settings
// so have the existing value in memory so we can merge the new value
const deleteSettings: ThunkHandler<{
  org: string,
  user: string,
  fields: string[],
}, boolean> = ({
  org,
  user,
  fields,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete(`/booking/settings/${org}/${user}?fields=${fields.join(',')}`)
  return true
}

const loadBookings: ThunkHandler<IData_Paginated_Query_Wrapper<IQuery_Org_Scope & IQuery_Booking_List>, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const {
    pagination,
    query,
  } = payload
  const paginationQueryParams = getPaginationQueryParams(pagination)
  const [searchQueryParams, [org, scope]] = stripObjectValues(query, ['org', 'scope'])
  if(!org) throw new Error(`org required for loadBookings`)
  if(!scope) throw new Error(`scope required for loadBookings`)
  const allQueryParams = Object.assign({}, paginationQueryParams, searchQueryParams)
  const data = await handlers.get<IData_Paginated_Results_Wrapper<IRecord_Booking_Core_Viewable>>(`/booking/list/${org}/${scope}`, {
    params: allQueryParams,
  })
  dispatch(actions.setBookingData(data))
}

const downloadBookingsCSV: ThunkHandler<IData_Paginated_Query_Wrapper<IQuery_Org_Scope & IQuery_Booking_List>, boolean> = (payload) => async (dispatch, getState) => {
  const {
    pagination,
    query,
  } = payload
  const paginationQueryParams = getPaginationQueryParams(pagination)
  const [searchQueryParams, [org, scope]] = stripObjectValues(query, ['org', 'scope'])
  const allQueryParams = Object.assign({}, paginationQueryParams, searchQueryParams)
  const queryString = Object
    .keys(allQueryParams)
    .map(key => `${key}=${encodeURIComponent(allQueryParams[key])}`)
    .join('&')
  const token = authSelectors.token(getState())
  const url = getProjectUrl(getState) + `/booking/csv/${org}/${scope}?access_token=${token}&${queryString}`
  window.open(url)
}

const loadReporting: ThunkHandler<IQuery_Org_Scope & IQuery_Booking_List, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const [searchQueryParams, [org, scope]] = stripObjectValues(query, ['org', 'scope'])
  if(!org) throw new Error(`org required for loadBookings`)
  if(!scope) throw new Error(`scope required for loadBookings`)
  console.dir(searchQueryParams)
  const data = await handlers.get<IData_Booking_Reporting[]>(`/booking/reporting/${org}/${scope}`, {
    params: searchQueryParams,
  })
  dispatch(actions.setReportingData(data))
}

const getBooking: ThunkHandler<{
  org: string,
  id: string,
}, IRecord_Booking_Core_Viewable> = ({
  org,
  id,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_Booking_Core_Viewable>(`/booking/record/${org}/${id}`)
  return data
}

const search: ThunkHandler<PayloadSearch, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IElasticSearchResult<IElasticSearch_Source_Booking>[]>(`/booking/search/${payload.org}`, {
    params: {
      search: payload.query,
      scope: ROLE_SCOPE_DESCENDENTS,
    }
  })
  dispatch(actions.setSearchResults(data))
}

const createBooking: ThunkHandler<{
  org: string,
  data: IRecord_Booking_Core_Editable,
}, boolean | ICreateBookingCheckEmailResponse> = ({
  org,
  data,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post(`/booking/data/${org}`, data)
  return true
}

const updateBooking: ThunkHandler<{
  org: string,
  id: string,
  data: IRecord_Booking_Core_Editable,
}, boolean | ICreateBookingCheckEmailResponse> = ({
  org,
  id,
  data,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put(`/booking/data/${org}/${id}`, data)
  return true
}

const deleteBooking: ThunkHandler<{
  org: string,
  id: string,
}, boolean> = ({
  org,
  id,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete(`/booking/data/${org}/${id}`)
  return true
}

const loadPublicCalendar: ThunkHandler<{
  org: string,
  date: string,
  days?: number,
  length?: number,
}, void> = ({
  org,
  date,
  days = BOOKING_DEFAULT_CALENDAR_DAYS,
  length = BOOKING_DEFAULT_CALENDAR_LENGTH,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Calendar_Public_Day[]>(`/booking/calendar/${org}/public`, {
    params: {
      date,
      days,
      length,
    },
  })
  dispatch(actions.setPublicCalendar(data))
}

const loadCalendarExportLinks: ThunkHandler<{
  org: string,
  et?: string,
}, void> = ({
  org,
  et,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  let url = `/booking/calendar/${org}/export_link`
  if(et) {
    url += `/user/${et}`
  }
  const data = await handlers.get<IData_Booking_Calendar_Export_Links>(url)
  dispatch(actions.setCalendarExportLinks(data))
}

const listTags: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_CRMTag[]>(`/booking/tags`)
  dispatch(actions.setTags(data))
}

/*

  collections

*/
const reducers = {
  setSettings,
  setHistoricSettings,
  setBookingData,
  setReportingData,
  setSearchResults,
  setAdminCalendar,
  setPublicCalendar,
  setCalendarExportLinks,
  setTags,
}

export const actions = {
  setSettings: CreateAction(prefix, `setSettings`, reducers.setSettings),
  setHistoricSettings: CreateAction(prefix, `setHistoricSettings`, reducers.setHistoricSettings),
  setBookingData: CreateAction(prefix, `setBookingData`, reducers.setBookingData),
  setReportingData: CreateAction(prefix, `setReportingData`, reducers.setReportingData),
  setSearchResults: CreateAction(prefix, `setSearchResults`, reducers.setSearchResults),
  setAdminCalendar: CreateAction(prefix, `setAdminCalendar`, reducers.setAdminCalendar),
  setPublicCalendar: CreateAction(prefix, `setPublicCalendar`, reducers.setPublicCalendar),
  setCalendarExportLinks: CreateAction(prefix, `setCalendarExportLinks`, reducers.setCalendarExportLinks),
  setTags: CreateAction(prefix, `setTags`, reducers.setTags),

  loadAllOrgSettings: NetworkThunk(prefix, 'loadAllOrgSettings', loadAllOrgSettings, {
    snackbar: true,
  }),
  loadSingleOrgSettings: NetworkThunk(prefix, 'loadSingleOrgSettings', loadSingleOrgSettings, {
    snackbar: true,
  }),
  loadSingleOrgSettingsHistoric: NetworkThunk(prefix, 'loadSingleOrgSettingsHistoric', loadSingleOrgSettingsHistoric, {
    snackbar: true,
  }),
  loadPublicCalendar: NetworkThunk(prefix, 'loadPublicCalendar', loadPublicCalendar, {
    snackbar: true,
  }),
  listTags: NetworkThunk(prefix, 'listTags', listTags, {
    snackbar: true,
  }),
  loadCalendarExportLinks: NetworkThunk(prefix, 'loadCalendarExportLinks', loadCalendarExportLinks, {
    snackbar: true,
    globalLoading: {
      message: 'loading...'
    },
  }),
  updateSettings: NetworkThunk(prefix, 'updateSettings', updateSettings, {
    snackbar: true,
    globalLoading: {
      message: 'updating settings...'
    },
  }),
  deleteSettings: NetworkThunk(prefix, 'deleteSettings', deleteSettings, {
    snackbar: true,
    globalLoading: {
      message: 'deleting settings...'
    },
  }),
  loadBookings: NetworkThunk(prefix, 'loadBookings', loadBookings, {
    snackbar: true,
  }),
  downloadBookingsCSV: NetworkThunk(prefix, 'downloadBookingsCSV', downloadBookingsCSV, {
    snackbar: true,
  }),
  loadReporting: NetworkThunk(prefix, 'loadReporting', loadReporting, {
    snackbar: true,
  }),
  getBooking: NetworkThunk(prefix, 'getBooking', getBooking, {
    snackbar: true,
  }),
  search: NetworkThunk(prefix, 'search', search, {
    snackbar: true,
  }),
  createBooking: NetworkThunk(prefix, 'createBooking', createBooking, {
    snackbar: true,
    globalLoading: {
      message: 'creating booking...'
    },
  }),
  updateBooking: NetworkThunk(prefix, 'updateBooking', updateBooking, {
    snackbar: true,
    globalLoading: {
      message: 'updating booking...'
    },
  }),
  deleteBooking: NetworkThunk(prefix, 'deleteBooking', deleteBooking, {
    snackbar: true,
    globalLoading: {
      message: 'deleting booking...'
    },
  }),
}

export const reducer = CreateReducer<BookingState>({
  prefix,
  initialState,
  reducers,
})

export default actions
